import { motion } from 'framer-motion'
import { Badge, Tag } from './ui'
import { LAB_REGISTRY } from './lab/registry'
import { useT } from '../lib/i18n'
import { INDUSTRY_PARADIGMS, type IndustryId } from '../data/industryParadigms'
import type { LabDemosLocale, Locale, ParadigmLocale } from '../lib/locales'

interface IndustryDetailPageProps {
  industryId: IndustryId
  onBackToHub: () => void
}

/**
 * Resolve the demo locale slice for an industry/paradigm pair.
 * - Industry-specific overrides take precedence.
 * - 'energy' falls back to the default labDemos (which is the petroleum scenario).
 * - Other industries with no override → null (render "coming soon").
 */
function kebabToCamel(s: string): string {
  return s.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
}

function getDemoData(
  t: Locale,
  industryId: IndustryId,
  paradigmId: string,
): unknown | null {
  const key = kebabToCamel(paradigmId) as keyof LabDemosLocale
  const override = t.industryDemos?.[industryId]?.demos?.[key]
  if (override) return override
  if (industryId === 'energy') return t.labDemos[key]
  return null
}

export function IndustryDetailPage({ industryId, onBackToHub }: IndustryDetailPageProps) {
  const t = useT()
  const industry = t.industries.find((i) => i.id === industryId)
  const paradigmIds = INDUSTRY_PARADIGMS[industryId]
  const paradigmById = new Map(t.paradigms.paradigms.map((p) => [p.id, p]))

  if (!industry) return null

  return (
    <main className="mx-auto max-w-6xl px-6 sm:px-10 pt-12 sm:pt-16 pb-24">
      <header className="mb-12 max-w-4xl">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <button
            type="button"
            onClick={onBackToHub}
            className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] transition-colors"
          >
            ← {t.nav.industries}
          </button>
          <Tag kind="rule">{industry.code}</Tag>
          <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
            {industry.nameEn}
          </span>
        </div>
        <h1
          className="text-[color:var(--color-orion-text)] leading-[1.1] mb-4"
          style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
        >
          {industry.name}
        </h1>
        <p
          className="text-[color:var(--color-orion-haze)] mb-3"
          style={{ fontSize: 'var(--text-lead)', lineHeight: 1.55 }}
        >
          {industry.benefit}
        </p>
        <div className="surface-night border-faint rounded-[var(--radius-md)] p-4 mt-5">
          <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-1.5">
            {t.home.industriesSection.labels.scenario}
          </div>
          <p
            className="text-[color:var(--color-orion-text)]"
            style={{ fontSize: 'var(--text-small)', lineHeight: 1.55 }}
          >
            {industry.scenario}
          </p>
        </div>
      </header>

      <div className="space-y-10">
        {paradigmIds.map((paradigmId, idx) => {
          const paradigm = paradigmById.get(paradigmId)
          if (!paradigm) return null
          const data = getDemoData(t, industryId, paradigmId)
          return (
            <motion.section
              id={paradigmId}
              key={paradigmId}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 0.35, delay: idx * 0.05 }}
              className="scroll-mt-24"
            >
              {data ? (
                <DemoMount paradigm={paradigm} data={data} />
              ) : (
                <ComingSoonSlot paradigm={paradigm} />
              )}
            </motion.section>
          )
        })}
      </div>
    </main>
  )
}

function DemoMount({ paradigm, data }: { paradigm: ParadigmLocale; data: unknown }) {
  const t = useT()
  const DemoComponent = LAB_REGISTRY[paradigm.id]
  if (!DemoComponent) return null
  return <DemoComponent paradigm={paradigm} lab={t.lab} data={data} />
}

function ComingSoonSlot({ paradigm }: { paradigm: ParadigmLocale }) {
  const t = useT()
  return (
    <div className="surface-night border-faint rounded-[var(--radius-lg)] p-6">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-baseline gap-3">
          <span className="mono text-[24px] leading-none text-[color:var(--color-orion-haze)] font-light">
            {paradigm.number}
          </span>
          <h2
            className="text-[color:var(--color-orion-text)] font-medium"
            style={{ fontSize: 'var(--text-h3)' }}
          >
            {paradigm.title}
          </h2>
        </div>
        <Badge tone="neutral">{t.lab.comingSoonLabel}</Badge>
      </div>
      <p
        className="text-[color:var(--color-orion-haze)]"
        style={{ fontSize: 'var(--text-body)', lineHeight: 1.55 }}
      >
        {paradigm.tagline}
      </p>
    </div>
  )
}
